import { ReportContext } from '../internal/ReportContext';
import { ReplaySubject } from 'rxjs';
import { ExportDataOptions, FormatType } from '../utils/exportDataUtils';
import { waitForReportSessionReady, assertValidContext } from './handleUtils';
import { ExportPDFOptions } from '../utils/printUtils';

/**
 * @public @sealed
 */
export class ObjectHandle {
  /**
   * @internal
   */
  private _context: ReportContext | null;
  /**
   * @internal
   */
  private _destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  /**
   * @internal
   */
  private _objectName: string;
  /**
   * @internal
   */
  private _eventListeners: Map<
    string,
    Set<{
      callback: (event: { type: string; target: ObjectHandle }) => void;
      handle: { detach: () => void };
    }>
  > = new Map();

  /**
   * @internal
   */
  constructor(context: ReportContext, objectName: string) {
    this._context = context;
    this._objectName = objectName;
  }

  /**
   * @internal
   */
  get objectName(): string {
    return this._objectName;
  }

  get readyState(): 'loading' | 'contentLoading' | 'complete' | 'error' {
    if (!this._context) {
      return 'error';
    }
    return this._context.getReadyState(this._objectName);
  }

  /**
   * @internal
   */
  async canExportData(): Promise<boolean> {
    assertValidContext(this._context);
    await waitForReportSessionReady(this._context, this._destroyed$);
    return this._context.canExportData(this._objectName);
  }

  async exportData(format: FormatType, options?: ExportDataOptions): Promise<string> {
    assertValidContext(this._context);
    await waitForReportSessionReady(this._context, this._destroyed$);
    return this._context.exportData(this._objectName, format, options);
  }

  async exportPDF(options?: Omit<ExportPDFOptions, 'includedReportObjects'>): Promise<string> {
    assertValidContext(this._context);
    await waitForReportSessionReady(this._context, this._destroyed$);
    return this._context.exportPDF({ ...options, includedReportObjects: [this._objectName] });
  }

  async getExportDataInfo(): Promise<{
    rowCount: number;
    columnInfo: { name: string; label: string }[];
  }> {
    assertValidContext(this._context);
    return this._context.getExportDataInfo(this._objectName);
  }

  refreshData() {
    assertValidContext(this._context);
    if (this._context.getSessionStatusNotifier().getValue() !== 'ready') {
      // no-op if the report has not loaded or has errored
      return;
    }
    const reportController = this._context.getReportController();
    const controller = reportController?.getVisualElementControllerForVisualElementName(
      this._objectName
    );
    controller
      ?.getPrimaryVisualElementController() // Refresh the primary controller for composites
      ?.refreshData();
  }

  getSelectedData(options?: { formatData?: boolean | 'datesOnly' }) {
    assertValidContext(this._context);
    return this._context.getData(this._objectName, options?.formatData, true);
  }

  getData(options?: { formatData?: boolean | 'datesOnly' }) {
    assertValidContext(this._context);
    return this._context.getData(this._objectName, options?.formatData, false);
  }

  addEventListener(
    type: 'selectionChanged' | 'readyStateChanged',
    callback: (event: { type: string; target: ObjectHandle }) => void
  ) {
    assertValidContext(this._context);
    const events = this._eventListeners.get(type) || new Set();
    for (const event of events) {
      if (event.callback === callback) {
        return;
      }
    }
    const eventDetachHandle = this._context.subscribeEvent(type, {
      type: 'object',
      callback,
      objectHandle: this,
    });
    if (eventDetachHandle) {
      this._eventListeners.set(type, events.add({ callback, handle: eventDetachHandle }));
    }
  }

  removeEventListener(
    type: 'selectionChanged' | 'readyStateChanged',
    callback: (event: { type: string; target: ObjectHandle }) => void
  ) {
    const events = this._eventListeners.get(type);
    if (!events) {
      return;
    }
    for (const event of events) {
      if (event.callback === callback) {
        event.handle.detach();
        events.delete(event);
        break;
      }
    }
  }

  /**
   * Prevents the handle from being used after the context goes out of scope
   * @internal
   */
  destroy() {
    for (const eventListener of this._eventListeners.values()) {
      for (const eventInfo of eventListener.values()) {
        eventInfo.handle.detach();
      }
    }
    this._eventListeners.clear();
    this._context = null;
    this._destroyed$.next(true);
  }
}
